import type { ErrorPageProps } from "@@/backoffice/shared/error-page";
import {
    ErrorBoundaryMessage,
    ErrorPage,
} from "@@/backoffice/shared/error-page";
import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { useLocation } from "react-router";
import type { Except } from "type-fest";

const SentryErrorBoundary = ({
    children,
    ...props
}: Except<Sentry.ErrorBoundaryProps, "fallback"> & {
    errorPageProps?: (currentProps: ErrorPageProps) => ErrorPageProps;
}) => {
    const location = useLocation();
    const initErrorLocation = useRef(location.pathname + location.search);

    return (
        <Sentry.ErrorBoundary
            {...props}
            fallback={errorInfo => {
                const currentProps: ErrorPageProps = {
                    error: errorInfo.error,
                    initErrorLocation: initErrorLocation.current,
                    resetError: () => {
                        initErrorLocation.current =
                            location.pathname + location.search;
                        errorInfo.resetError();
                    },
                };
                return (
                    <ErrorPage
                        {...(props.errorPageProps
                            ? props.errorPageProps(currentProps)
                            : currentProps)}
                    />
                );
            }}>
            {children}
        </Sentry.ErrorBoundary>
    );
};

const SentryErrorBoundaryForPartialView = ({
    children,
    ...props
}: Except<Sentry.ErrorBoundaryProps, "fallback"> & {
    errorPageProps?: (currentProps: ErrorPageProps) => ErrorPageProps;
}) => {
    const location = useLocation();
    const initErrorLocation = useRef(location.pathname + location.search);

    return (
        <Sentry.ErrorBoundary
            {...props}
            fallback={errorInfo => {
                const currentProps: ErrorPageProps = {
                    error: errorInfo.error,
                    initErrorLocation: initErrorLocation.current,
                    resetError: () => {
                        initErrorLocation.current =
                            location.pathname + location.search;
                        errorInfo.resetError();
                    },
                };
                return (
                    <ErrorBoundaryMessage
                        {...(props.errorPageProps
                            ? props.errorPageProps(currentProps)
                            : currentProps)}
                    />
                );
            }}>
            {children}
        </Sentry.ErrorBoundary>
    );
};

export { SentryErrorBoundary, SentryErrorBoundaryForPartialView };
